import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Target, TrendingUp, Zap, Heart, Award, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnimatedSection = ({ children, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    viewport={{ once: true, margin: '-100px' }}
    className={className}
  >
    {children}
  </motion.div>
);

export const HomePage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLeadMagnetSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/lead-magnet`, { email });
      toast.success('Success! Check your email for the Brand Clarity Scorecard.');
      setEmail('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="homepage" className="w-full">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative bg-[#F7FAFC] px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-sm uppercase tracking-[0.2em] font-medium text-[#1A9FE0] mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Strategy-Led Marketing for Ambitious Brands
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-[#0F2A4A] font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Your Marketing Should Make You <span style={{ background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Impossible to Ignore.</span>
              </h1>
              <p className="text-lg text-[#5A6B7E] leading-relaxed mb-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                The B2B Digital builds bold, strategy-first marketing for founders and businesses who are done with vague messaging, random tactics, and forgettable presence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center justify-center bg-[#3FC9E8] text-[#0F2A4A] font-medium px-8 py-4 hover:bg-[#1A9FE0] hover:text-white transition-all duration-300 active:scale-95"
                >
                  Book a FREE Strategy Call <ArrowRight className="ml-2" size={20} />
                </Link>
                <a
                  href="#lead-magnet"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center justify-center border-2 border-[#0A4DA6] text-[#0A4DA6] bg-transparent hover:bg-[#0A4DA6] hover:text-white font-medium px-8 py-4 transition-all duration-300 active:scale-95"
                >
                  Get The Brand Clarity Scorecard
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1694702740570-0a31ee1525c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODA2NjY4MjJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Modern business strategy"
                className="w-full h-auto rounded-sm shadow-2xl"
              />
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-[#3FC9E8]/20 blur-3xl rounded-full" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#1A9FE0]/20 blur-3xl rounded-full" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stakes / Problem Section - Diagnostic Style */}
      <section data-testid="problem-section" className="relative bg-white px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        {/* Decorative grid pattern */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(#0F2A4A 1px, transparent 1px), linear-gradient(90deg, #0F2A4A 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />

        <div className="max-w-7xl mx-auto relative">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-12 gap-8 mb-20"
          >
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#3FC9E8]" />
                <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#1A9FE0]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Diagnosis</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0F2A4A] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                The real problem isn't <span className="relative inline-block">
                  <span className="relative z-10">your product.</span>
                  <span className="absolute bottom-2 left-0 right-0 h-3 bg-[#3FC9E8]/40 -z-0" />
                </span>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-lg text-[#5A6B7E] leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Most B2B businesses fail at marketing for three connected reasons. Recognize any of these?
              </p>
            </div>
          </motion.div>

          {/* Diagnostic Cards Grid */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 relative">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-12 left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-[#3FC9E8] via-[#1A9FE0] to-[#0A4DA6] opacity-30" />

            {[
              {
                num: '01',
                tag: 'EXTERNAL',
                color: '#3FC9E8',
                title: 'Your marketing doesn\'t match your business.',
                body: 'The website is vague. The messaging is generic. The strategy, if you have one, is a loose collection of tactics that never fully connect.',
                highlight: ['vague', 'generic'],
                rotate: '-1deg'
              },
              {
                num: '02',
                tag: 'INTERNAL',
                color: '#1A9FE0',
                title: 'You\'ve been burned before.',
                body: 'You\'ve invested in marketing and watched it produce nothing useful. You\'re not sure if anyone understood your business, or if any of it was moving you toward something real.',
                highlight: ['burned', 'exhausting'],
                rotate: '0deg'
              },
              {
                num: '03',
                tag: 'PHILOSOPHICAL',
                color: '#0A4DA6',
                title: 'You shouldn\'t have to settle.',
                body: 'Every serious business deserves communication that is clear, sharp, and built with a real plan behind it. Not marketing that keeps you busy but never builds momentum.',
                highlight: ['settle'],
                rotate: '1deg'
              }
            ].map((card, idx) => (
              <motion.div
                key={card.num}
                data-testid={`problem-card-${idx + 1}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                viewport={{ once: true, margin: '-80px' }}
                whileHover={{ y: -8 }}
                className="relative bg-[#F7FAFC] p-8 lg:p-10 group cursor-default"
                style={{ transform: `rotate(${card.rotate})` }}
              >
                {/* Number Badge */}
                <div className="flex items-center justify-between mb-8">
                  <div
                    className="w-14 h-14 flex items-center justify-center text-white font-bold text-lg relative z-10"
                    style={{ backgroundColor: card.color, fontFamily: 'Outfit, sans-serif' }}
                  >
                    {card.num}
                  </div>
                  <span
                    className="text-[10px] font-bold tracking-[0.25em] px-3 py-1.5 border"
                    style={{ color: card.color, borderColor: card.color, fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    {card.tag}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-xl lg:text-2xl font-bold text-[#0F2A4A] mb-4 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {card.title}
                </h3>

                {/* Body */}
                <p className="text-[#5A6B7E] leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {card.body}
                </p>

                {/* Animated bottom bar */}
                <div className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500" style={{ backgroundColor: card.color }} />

                {/* Huge background number */}
                <span
                  className="absolute -top-4 -right-2 text-[120px] font-bold leading-none opacity-[0.04] pointer-events-none select-none"
                  style={{ color: card.color, fontFamily: 'Outfit, sans-serif' }}
                >
                  {card.num}
                </span>
              </motion.div>
            ))}
          </div>

          {/* Closing Statement */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-20 flex flex-col md:flex-row items-center justify-center gap-4 text-center"
          >
            <div className="h-px w-16 bg-[#3FC9E8]" />
            <p className="text-xl md:text-2xl text-[#0F2A4A] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              When your message doesn't land, neither does your revenue.
            </p>
            <p className="text-xl md:text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Let's fix that.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Guide / Empathy + Authority - Two Voices */}
      <section data-testid="guide-section" className="relative bg-[#0F2A4A] text-white px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        {/* Background radial glows */}
        <div className="absolute top-20 -left-32 w-96 h-96 bg-[#3FC9E8]/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute bottom-20 -right-32 w-96 h-96 bg-[#1A9FE0]/10 blur-[120px] rounded-full pointer-events-none" />

        {/* Background dot pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
          backgroundImage: 'radial-gradient(#3FC9E8 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }} />

        <div className="max-w-7xl mx-auto relative">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-12 gap-8 mb-20"
          >
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#3FC9E8]" />
                <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#3FC9E8]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>The Guide</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                We've seen this before. <span style={{ background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>And we know how to fix it.</span>
              </h2>
            </div>
          </motion.div>

          {/* Two Voices: Empathy & Authority */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-20 relative">
            {/* Connecting "AND" badge in center on desktop */}
            <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-[#0F2A4A] border-2 border-[#3FC9E8] flex items-center justify-center">
                <span className="text-xs font-bold text-[#3FC9E8] tracking-widest" style={{ fontFamily: 'Outfit, sans-serif' }}>+</span>
              </div>
            </div>

            {/* Empathy Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true, margin: '-80px' }}
              whileHover={{ y: -8 }}
              className="relative bg-white/[0.03] border border-white/10 backdrop-blur-sm p-8 lg:p-10 group"
            >
              {/* Subtle inner glow */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#3FC9E8] to-transparent" />

              {/* Icon Header */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-full bg-[#3FC9E8]/15 border border-[#3FC9E8]/30 flex items-center justify-center">
                  <Heart className="text-[#3FC9E8]" size={22} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#3FC9E8] font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>We Get It</p>
                  <h3 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Empathy</h3>
                </div>
              </div>

              {/* Large opening quote */}
              <div className="text-6xl leading-none text-[#3FC9E8]/30 font-bold mb-2 select-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
                "
              </div>

              {/* Content */}
              <p className="text-white/85 leading-relaxed mb-4 -mt-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                We understand what it feels like to invest in marketing and watch it produce nothing useful. To brief an agency that talks beautifully about <em className="text-[#3FC9E8] not-italic font-medium">"storytelling"</em> and <em className="text-[#3FC9E8] not-italic font-medium">"brand love"</em>, then delivers work that completely misses what your business actually needs.
              </p>
              <p className="text-white/70 leading-relaxed text-sm pt-4 border-t border-white/10" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                The B2B Digital was built specifically to solve this. We start with strategy, not activity. We build with precision, not guesswork.
              </p>
            </motion.div>

            {/* Authority Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true, margin: '-80px' }}
              whileHover={{ y: -8 }}
              className="relative bg-[#3FC9E8] text-[#0F2A4A] p-8 lg:p-10 group overflow-hidden"
            >
              {/* Background shine line */}
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-[#0F2A4A] to-transparent" />

              {/* Icon Header */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-full bg-[#0F2A4A] flex items-center justify-center">
                  <Award className="text-[#3FC9E8]" size={22} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#0F2A4A]/70 font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>We Fix It</p>
                  <h3 className="text-3xl font-bold text-[#0F2A4A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Authority</h3>
                </div>
              </div>

              {/* Content */}
              <p className="text-[#0F2A4A] leading-relaxed mb-6 font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                We've helped founders clarify positioning from the ground up, taken businesses with good products and weak messaging and built them into recognisable brands with actual pipeline.
              </p>

              {/* Bullet capabilities */}
              <div className="space-y-3 pt-4 border-t border-[#0F2A4A]/15">
                {[
                  'Commercial intent behind every decision',
                  'Strategy before activity',
                  'Outcomes over output'
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="text-[#0F2A4A] flex-shrink-0" size={18} />
                    <span className="text-sm text-[#0F2A4A] font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{item}</span>
                  </div>
                ))}
              </div>

              {/* Decorative number */}
              <div className="absolute -bottom-6 -right-2 text-[140px] leading-none font-bold text-[#0F2A4A]/8 select-none pointer-events-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
                ✓
              </div>
            </motion.div>
          </div>

          {/* Trust Bar - Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Section label */}
            <div className="flex items-center justify-center gap-4 mb-10">
              <div className="h-px w-16 bg-[#3FC9E8]/30" />
              <p className="text-xs uppercase tracking-[0.3em] text-[#3FC9E8] font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Track Record</p>
              <div className="h-px w-16 bg-[#3FC9E8]/30" />
            </div>

            {/* Stats Grid with vertical dividers */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0">
              {[
                { value: '50+', label: 'Brands Positioned', icon: Target, suffix: 'across industries' },
                { value: '100+', label: 'Campaigns Delivered', icon: Sparkles, suffix: 'measurable outcomes' },
                { value: '250%', label: 'Avg Lead Lift', icon: TrendingUp, suffix: 'qualified pipeline' }
              ].map((stat, idx) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + idx * 0.15 }}
                  viewport={{ once: true }}
                  className={`text-center md:px-8 ${idx > 0 ? 'md:border-l md:border-white/10' : ''}`}
                >
                  {/* Icon */}
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#3FC9E8]/10 border border-[#3FC9E8]/20 mb-4">
                    <stat.icon className="text-[#3FC9E8]" size={20} />
                  </div>

                  {/* Big Number */}
                  <p
                    className="text-5xl lg:text-6xl font-bold mb-2 leading-none"
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    {stat.value}
                  </p>

                  {/* Label */}
                  <p className="text-white font-medium mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.label}</p>
                  <p className="text-xs text-white/50 uppercase tracking-wider" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{stat.suffix}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Value Proposition / Benefits */}
      <AnimatedSection>
        <section data-testid="benefits-section" className="bg-[#F7FAFC] px-6 md:px-12 lg:px-24 py-20 lg:py-32">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-[#0F2A4A] font-bold mb-16 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What You Get When Strategy Comes First.
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                whileHover={{ y: -8 }}
                className="bg-white p-8 border border-[#5A6B7E]/20 hover:shadow-lg transition-all duration-300"
                data-testid="benefit-card-1"
              >
                <div className="w-12 h-12 bg-[#3FC9E8]/10 flex items-center justify-center mb-6">
                  <Target className="text-[#3FC9E8]" size={24} />
                </div>
                <h3 className="text-xl font-bold text-[#0A4DA6] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Clarity That Converts</h3>
                <p className="text-base text-[#5A6B7E] leading-relaxed mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  When your message is clear, your audience self-selects. You stop attracting the wrong clients, wasting budget on the wrong channels, and explaining what you do on every sales call.
                </p>
                <p className="text-sm text-[#5A6B7E]/70" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Feature: Brand positioning, messaging frameworks, and ICP-led strategy.
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -8 }}
                className="bg-white p-8 border border-[#5A6B7E]/20 hover:shadow-lg transition-all duration-300"
                data-testid="benefit-card-2"
              >
                <div className="w-12 h-12 bg-[#1A9FE0]/10 flex items-center justify-center mb-6">
                  <TrendingUp className="text-[#1A9FE0]" size={24} />
                </div>
                <h3 className="text-xl font-bold text-[#0A4DA6] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Marketing That Actually Builds Something</h3>
                <p className="text-base text-[#5A6B7E] leading-relaxed mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Every asset we create connects to a bigger picture. No random content. No disconnected campaigns. Just deliberate work that compounds into authority, trust, and qualified demand.
                </p>
                <p className="text-sm text-[#5A6B7E]/70" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Feature: Integrated content strategy, SEO, demand generation, lead magnet systems.
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -8 }}
                className="bg-white p-8 border border-[#5A6B7E]/20 hover:shadow-lg transition-all duration-300"
                data-testid="benefit-card-3"
              >
                <div className="w-12 h-12 bg-[#0A4DA6]/10 flex items-center justify-center mb-6">
                  <Zap className="text-[#0A4DA6]" size={24} />
                </div>
                <h3 className="text-xl font-bold text-[#0A4DA6] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>You Always Know What's Working</h3>
                <p className="text-base text-[#5A6B7E] leading-relaxed mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  No more guessing. No more chasing metrics that don't mean anything. You get a clear view of performance, a strategic partner who tells you the truth, and a growth plan that moves at the pace your business needs.
                </p>
                <p className="text-sm text-[#5A6B7E]/70" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Feature: Performance tracking, quarterly growth planning, ongoing retainer support.
                </p>
              </motion.div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* The Plan - 3 Step Journey */}
      <section data-testid="process-section" className="relative bg-white px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(#0F2A4A 1px, transparent 1px), linear-gradient(90deg, #0F2A4A 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />

        <div className="max-w-7xl mx-auto relative">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-12 gap-8 mb-20"
          >
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#3FC9E8]" />
                <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#1A9FE0]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Your Path</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0F2A4A] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Getting started is <span style={{ background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>simpler</span> than you think.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-lg text-[#5A6B7E] leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Three honest steps from where you are now to becoming the brand your market can't ignore.
              </p>
            </div>
          </motion.div>

          {/* Journey Path - Ascending Stepper */}
          <div className="relative">
            {/* Animated SVG path connecting steps */}
            <svg
              className="hidden md:block absolute top-1/2 left-0 w-full pointer-events-none"
              height="100"
              style={{ marginTop: '-50px' }}
              viewBox="0 0 1200 100"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="path-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#3FC9E8" />
                  <stop offset="50%" stopColor="#1A9FE0" />
                  <stop offset="100%" stopColor="#0A4DA6" />
                </linearGradient>
              </defs>
              <motion.path
                d="M 100 80 Q 400 80, 600 50 T 1100 20"
                stroke="url(#path-gradient)"
                strokeWidth="2"
                strokeDasharray="6 6"
                fill="none"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                transition={{ duration: 2, ease: 'easeInOut' }}
                viewport={{ once: true, margin: '-100px' }}
              />
            </svg>

            <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative">
              {[
                {
                  num: '01',
                  color: '#3FC9E8',
                  step: 'STEP ONE',
                  title: 'Book a FREE Strategy Call',
                  body: 'Tell us where you are, where you want to go, and what\'s been getting in the way. No sales pitch. No agency theatre. Just an honest conversation about your business.',
                  duration: '30 min',
                  offset: 'md:translate-y-12'
                },
                {
                  num: '02',
                  color: '#1A9FE0',
                  step: 'STEP TWO',
                  title: 'We Build Your Growth Plan',
                  body: 'We audit your current position, identify the gaps, and build a strategy designed around your specific goals — not a templated roadmap we reuse for every client.',
                  duration: '2-4 weeks',
                  offset: 'md:translate-y-6'
                },
                {
                  num: '03',
                  color: '#0A4DA6',
                  step: 'STEP THREE',
                  title: 'Impossible to Ignore',
                  body: 'With a clear message, the right channels, and marketing built on substance, you attract better clients, close faster, and grow with confidence.',
                  duration: 'Ongoing',
                  offset: ''
                }
              ].map((step, idx) => (
                <motion.div
                  key={step.num}
                  data-testid={`step-${idx + 1}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: idx * 0.2 }}
                  viewport={{ once: true, margin: '-80px' }}
                  className={`relative ${step.offset}`}
                >
                  {/* Card */}
                  <motion.div
                    whileHover={{ y: -8 }}
                    className="bg-white border border-[#5A6B7E]/15 p-8 relative group h-full"
                  >
                    {/* Number Circle */}
                    <div className="flex items-center justify-between mb-8">
                      <div className="relative">
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl relative z-10"
                          style={{ backgroundColor: step.color, fontFamily: 'Outfit, sans-serif' }}
                        >
                          {step.num}
                        </div>
                        {/* Pulsing ring */}
                        <div
                          className="absolute inset-0 rounded-full animate-ping opacity-20"
                          style={{ backgroundColor: step.color }}
                        />
                      </div>
                      <span
                        className="text-[10px] font-bold tracking-[0.25em]"
                        style={{ color: step.color, fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        {step.step}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl font-bold text-[#0F2A4A] mb-4 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {step.title}
                    </h3>

                    {/* Body */}
                    <p className="text-[#5A6B7E] leading-relaxed mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      {step.body}
                    </p>

                    {/* Duration badge at bottom */}
                    <div className="flex items-center gap-2 pt-4 border-t border-[#5A6B7E]/10">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: step.color }} />
                      <span className="text-xs uppercase tracking-wider text-[#5A6B7E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        Time: <span className="font-bold text-[#0F2A4A]">{step.duration}</span>
                      </span>
                    </div>

                    {/* Bottom hover bar */}
                    <div className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500" style={{ backgroundColor: step.color }} />
                  </motion.div>

                  {/* Arrow between cards (mobile only) */}
                  {idx < 2 && (
                    <div className="md:hidden flex justify-center my-4">
                      <ArrowRight className="text-[#5A6B7E]/40 rotate-90" size={24} />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Destination CTA */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="flex flex-col items-center mt-20"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-2 h-2 rounded-full bg-[#3FC9E8]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#5A6B7E] font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Start Here</span>
              <div className="w-2 h-2 rounded-full bg-[#3FC9E8]" />
            </div>
            <Link
              to="/contact"
              data-testid="process-cta"
              className="inline-flex items-center justify-center bg-[#0F2A4A] text-white font-medium px-10 py-5 hover:bg-[#0A4DA6] transition-all duration-300 active:scale-95 group"
            >
              Book a FREE Strategy Call
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Lead Magnet Section */}
      <AnimatedSection>
        <section id="lead-magnet" data-testid="lead-magnet-section" className="bg-[#3FC9E8] px-6 md:px-12 lg:px-24 py-20 lg:py-32">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-[#0F2A4A] font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The Brand Clarity Scorecard
            </h2>
            <p className="text-xl text-[#0F2A4A]/80 mb-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Find out why your marketing isn't converting, and exactly what to fix first.
            </p>
            <p className="text-base text-[#0F2A4A]/70 mb-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Answer 10 questions about your positioning, messaging, and customer journey. Get an instant score and a personalised breakdown of the gaps holding your growth back. Built for founders, marketing leads, and business owners who suspect the problem isn't budget — it's clarity.
            </p>
            <form onSubmit={handleLeadMagnetSubmit} className="max-w-md mx-auto" data-testid="lead-magnet-form">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your business email"
                  data-testid="lead-magnet-email-input"
                  className="flex-1 px-6 py-4 bg-white text-[#0F2A4A] placeholder-[#5A6B7E]/50 border-2 border-transparent focus:outline-none focus:ring-2 focus:ring-[#1A9FE0]"
                  required
                />
                <button
                  type="submit"
                  data-testid="lead-magnet-submit"
                  disabled={isSubmitting}
                  className="px-8 py-4 bg-[#0F2A4A] text-white font-medium hover:bg-[#0A4DA6] transition-all duration-300 active:scale-95 disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : 'Send Me the Scorecard'}
                </button>
              </div>
              <p className="text-sm text-[#0F2A4A]/60 mt-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                No spam. No sequences unless you opt in. Just the tool.
              </p>
            </form>
          </div>
        </section>
      </AnimatedSection>

      {/* Testimonials - Magazine Style */}
      <section data-testid="testimonials-section" className="relative bg-[#F7FAFC] px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        {/* Decorative dots */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: 'radial-gradient(#0F2A4A 1.5px, transparent 1.5px)',
          backgroundSize: '24px 24px'
        }} />

        <div className="max-w-7xl mx-auto relative">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-12 gap-8 mb-20"
          >
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#3FC9E8]" />
                <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#1A9FE0]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Proof</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0F2A4A] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Results over <span style={{ background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>rhetoric.</span>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-lg text-[#5A6B7E] leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                We let our clients do the talking. Here's what happens when strategy actually comes first.
              </p>
            </div>
          </motion.div>

          {/* Asymmetric Testimonial Grid */}
          <div className="grid md:grid-cols-12 gap-6 lg:gap-8">
            {/* Featured Testimonial - Large Dark Card */}
            <motion.div
              data-testid="testimonial-1"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true, margin: '-80px' }}
              whileHover={{ y: -8 }}
              className="md:col-span-7 md:row-span-2 bg-[#0F2A4A] text-white p-10 lg:p-12 relative overflow-hidden group"
            >
              {/* Giant Quote Mark */}
              <div className="absolute top-6 right-6 text-[160px] leading-none font-bold text-[#3FC9E8]/15 select-none pointer-events-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
                "
              </div>

              {/* Metric Badge */}
              <div className="inline-flex items-center gap-3 mb-8 relative z-10">
                <div className="bg-[#3FC9E8] text-[#0F2A4A] px-4 py-2 font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  +2 Enterprise Clients
                </div>
                <span className="text-xs uppercase tracking-[0.25em] text-[#3FC9E8] font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Month 1
                </span>
              </div>

              {/* Quote */}
              <p className="text-xl lg:text-2xl leading-relaxed mb-10 relative z-10" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Before working with The B2B Digital, our messaging was all over the place. After the positioning work, we closed two enterprise clients in the first month with a pitch we finally felt confident in.
              </p>

              {/* Author */}
              <div className="flex items-center gap-4 relative z-10 pt-8 border-t border-white/10">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#3FC9E8] to-[#1A9FE0] flex items-center justify-center font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  SC
                </div>
                <div>
                  <p className="font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Sarah Chen</p>
                  <p className="text-sm text-white/70" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>VP Marketing · TechFlow Solutions</p>
                </div>
              </div>

              {/* Corner accent */}
              <div className="absolute top-0 left-0 w-16 h-16 overflow-hidden">
                <div className="absolute top-0 left-0 w-32 h-1 bg-[#3FC9E8]" />
                <div className="absolute top-0 left-0 w-1 h-32 bg-[#3FC9E8]" />
              </div>
            </motion.div>

            {/* Testimonial 2 - Light Card */}
            <motion.div
              data-testid="testimonial-2"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              viewport={{ once: true, margin: '-80px' }}
              whileHover={{ y: -8 }}
              className="md:col-span-5 bg-white p-8 relative group border border-[#5A6B7E]/10"
            >
              {/* Metric Badge */}
              <div className="inline-flex items-center gap-2 mb-6">
                <div className="bg-[#1A9FE0] text-white px-3 py-1.5 text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Qualified Leads in 6 Weeks
                </div>
              </div>

              {/* Big quote mark */}
              <div className="text-5xl leading-none text-[#1A9FE0]/20 mb-2 font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>"</div>

              {/* Quote */}
              <p className="text-[#0F2A4A] leading-relaxed mb-6 -mt-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                We'd spent months producing content getting views but no leads. They identified the disconnect immediately and rebuilt the funnel.
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 pt-4 border-t border-[#5A6B7E]/10">
                <div className="w-10 h-10 rounded-full bg-[#1A9FE0] flex items-center justify-center font-bold text-white text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  MR
                </div>
                <div>
                  <p className="font-bold text-[#0F2A4A] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Marcus Reid</p>
                  <p className="text-xs text-[#5A6B7E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Founder · DataSync Pro</p>
                </div>
              </div>

              {/* Bottom hover bar */}
              <div className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500 bg-[#1A9FE0]" />
            </motion.div>

            {/* Testimonial 3 - Cyan Stat Card */}
            <motion.div
              data-testid="testimonial-3"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true, margin: '-80px' }}
              whileHover={{ y: -8 }}
              className="md:col-span-5 bg-[#3FC9E8] p-8 relative group overflow-hidden"
            >
              {/* Big metric foreground */}
              <div className="relative z-10">
                <p className="text-xs uppercase tracking-[0.25em] text-[#0F2A4A]/70 font-bold mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Conversion Rate</p>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-5xl font-bold text-[#0F2A4A]" style={{ fontFamily: 'Outfit, sans-serif' }}>1.2%</span>
                  <ArrowRight className="text-[#0F2A4A]" size={24} />
                  <span className="text-5xl font-bold text-[#0F2A4A]" style={{ fontFamily: 'Outfit, sans-serif' }}>4.8%</span>
                </div>

                {/* Quote */}
                <p className="text-[#0F2A4A] leading-relaxed mb-6 italic" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  "They understood our ICP better than we did by the third call. The website messaging they developed took our conversion rate from 1.2% to 4.8%."
                </p>

                {/* Author */}
                <div className="flex items-center gap-3 pt-4 border-t border-[#0F2A4A]/15">
                  <div className="w-10 h-10 rounded-full bg-[#0F2A4A] flex items-center justify-center font-bold text-[#3FC9E8] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    JK
                  </div>
                  <div>
                    <p className="font-bold text-[#0F2A4A] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Jennifer Ko</p>
                    <p className="text-xs text-[#0F2A4A]/70" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>CMO · CloudBridge</p>
                  </div>
                </div>
              </div>

              {/* Background decorative number */}
              <div className="absolute -bottom-8 -right-4 text-[180px] leading-none font-bold text-white/10 select-none pointer-events-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
                4x
              </div>
            </motion.div>
          </div>

          {/* Stats Strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-12 lg:mt-16 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#5A6B7E]/15 bg-white border border-[#5A6B7E]/10"
          >
            {[
              { value: '50+', label: 'Brands Repositioned' },
              { value: '100+', label: 'Campaigns Delivered' },
              { value: '250%', label: 'Avg Lead Lift' },
              { value: '4.9/5', label: 'Client Satisfaction' }
            ].map((stat) => (
              <div key={stat.label} className="text-center p-6 lg:p-8">
                <p className="text-3xl lg:text-4xl font-bold text-[#0F2A4A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</p>
                <p className="text-xs uppercase tracking-wider text-[#5A6B7E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Closing CTA */}
      <AnimatedSection>
        <section data-testid="closing-cta-section" className="bg-[#0F2A4A] text-white px-6 md:px-12 lg:px-24 py-20 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The Gap Between Where You Are and Where You Should Be Is a Messaging Problem.
            </h2>
            <p className="text-lg text-white/80 leading-relaxed mb-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Every week your positioning stays vague, a better-messaged competitor takes the client you should have won. Every month your marketing stays disconnected from strategy, you spend money on activity instead of growth. You don't need more content. You need a clearer message, a smarter plan, and execution that actually builds something.
            </p>
            <p className="text-xl text-[#3FC9E8] font-semibold mb-12" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The B2B Digital exists for businesses ready to show up with force.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="closing-cta-primary"
                className="inline-flex items-center justify-center bg-[#3FC9E8] text-[#0F2A4A] font-medium px-8 py-4 hover:bg-[#1A9FE0] hover:text-white transition-all duration-300 active:scale-95"
              >
                Book a FREE Strategy Call <ArrowRight className="ml-2" size={20} />
              </Link>
              <a
                href="#lead-magnet"
                data-testid="closing-cta-secondary"
                className="inline-flex items-center justify-center border-2 border-white/30 text-white bg-transparent hover:bg-white hover:text-[#0F2A4A] font-medium px-8 py-4 transition-all duration-300 active:scale-95"
              >
                Get The Brand Clarity Scorecard
              </a>
            </div>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default HomePage;